// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {StateAction} from '@microsoft/agent-host-protocol'
import type {AhpTransport} from '@microsoft/agent-host-protocol/client'

export interface RelayCommandObserver {
  sent(channel: string, action: StateAction): void
  failed(): void
  unavailable(): void
}

const reported = new Set<string>()

export function reportClientInteractionError(
  reason: 'flag_evaluation' | 'observer' | 'emission' | 'clock' | 'observation_capacity',
): void {
  if (reported.has(reason)) return
  reported.add(reason)
  try {
    reportError(new Error(`Client interaction telemetry ${reason} failed`), {
      critical: false,
      reactAppName: 'copilot-client-interaction',
    })
  } catch {
    // eslint-disable-next-line no-console
    console.error('Unable to report client interaction telemetry error')
  }
}

export function isClientInteractionEnabled(): boolean {
  try {
    return isFeatureEnabled('one_copilot_browser_telemetry')
  } catch {
    reportClientInteractionError('flag_evaluation')
    return false
  }
}

export function observeClientInteraction<T>(operation: () => T): T | undefined {
  try {
    return operation()
  } catch {
    reportClientInteractionError('observer')
    return undefined
  }
}

interface DispatchScope {
  observer: RelayCommandObserver
  observed: boolean
}

let dispatchScope: DispatchScope | undefined
const actions = new WeakMap<object, {scope: DispatchScope; channel: string; action: StateAction}>()

export function withRelayCommandObserver<T>(observer: RelayCommandObserver | undefined, dispatch: () => T): T {
  if (!observer) return dispatch()
  const previous = dispatchScope
  const current: DispatchScope = {observer, observed: false}
  dispatchScope = current
  try {
    return dispatch()
  } catch (error) {
    observeClientInteraction(() => observer.failed())
    throw error
  } finally {
    dispatchScope = previous
    if (!current.observed) observeClientInteraction(() => observer.unavailable())
  }
}

export function observeRelayAction(channel: string, action: StateAction): void {
  if (!dispatchScope) return
  actions.set(action, {scope: dispatchScope, channel, action})
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function observeRelayTransport(transport: AhpTransport): AhpTransport {
  if (!isClientInteractionEnabled()) return transport
  return {
    recv: () => transport.recv(),
    close: () => transport.close(),
    send(message) {
      const params = record(message) && message.method === 'dispatchAction' ? message.params : undefined
      const action = record(params) && record(params.action) ? params.action : undefined
      const observation = action ? actions.get(action) : undefined
      if (action) actions.delete(action)
      if (!observation) return transport.send(message)
      const {scope, channel, action: dispatchedAction} = observation
      scope.observed = true
      const {observer} = scope
      const sent = () => observeClientInteraction(() => observer.sent(channel, dispatchedAction))
      const failed = () => observeClientInteraction(() => observer.failed())
      // The SDK's fire-and-forget dispatch return hides send failures. Observe
      // the real transport result, preserving its original return and rejection.
      let result: ReturnType<AhpTransport['send']>
      try {
        result = transport.send(message)
      } catch (error) {
        failed()
        throw error
      }
      try {
        if (result === undefined) sent()
        else if (result) {
          // eslint-disable-next-line github/no-then -- Keep the product's original promise and rejection unchanged.
          void result.then(sent, failed)
        } else {
          observeClientInteraction(() => observer.unavailable())
          reportClientInteractionError('observer')
        }
      } catch {
        observeClientInteraction(() => observer.unavailable())
        reportClientInteractionError('observer')
      }
      return result
    },
  }
}
